if (!customElements.get("product-vehicle-gallery")) {
  customElements.define(
    "product-vehicle-gallery",
    class ProductVehicleGallery extends HTMLElement {
      constructor() {
        super();
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onGalleryItemClick = this.onGalleryItemClick.bind(this);
        this.onModalClick = this.onModalClick.bind(this);
        this.onModalPrevClick = this.onModalPrevClick.bind(this);
        this.onModalNextClick = this.onModalNextClick.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.onCloseClick = () => this.hideModal();
        this.onModalTouchStart = this.onModalTouchStart.bind(this);
        this.onModalTouchEnd = this.onModalTouchEnd.bind(this);
        this.currentModalIndex = 0;
        this.modalTouchStartX = 0;
        this.modalTouchStartY = 0;
        this.modalIgnoreClick = false;
        this.preloadedImages = new Map();
        this.onGalleryItemWarm = this.onGalleryItemWarm.bind(this);
        this.onFlickitySelectPreload = this.onFlickitySelectPreload.bind(this);
      }

      connectedCallback() {
        this.enableModal = this.dataset.enableModal !== "false";
        this.mobileStaticGrid = this.dataset.mobileStaticGrid === "true";
        this.carousel = this.querySelector(".ppc-gallery-carousel");
        this.prevButton = this.querySelector(".js-prev");
        this.nextButton = this.querySelector(".js-next");
        this.modal = this.querySelector(".ppc-gallery-modal");
        this.modalShell = this.modal?.querySelector(".ppc-gallery-modal-shell");
        this.modalImage = this.modal?.querySelector(".ppc-gallery-modal-image");
        this.closeButton = this.modal?.querySelector(".ppc-gallery-modal-close");
        this.modalPrevButton = this.modal?.querySelector(".js-modal-prev");
        this.modalNextButton = this.modal?.querySelector(".js-modal-next");
        this.galleryItems = this.enableModal
          ? Array.from(this.querySelectorAll("[data-gallery-full]"))
          : [];

        if (this.enableModal) {
          this.bindModalEvents();
          this.observeGalleryVisibility();
          this.scheduleModalPreload();
        }

        if (!this.carousel || typeof Flickity === "undefined") {
          return;
        }

        this.columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 4;
        this.columnsTablet = parseInt(this.dataset.columnsTablet, 10) || 3;
        this.columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 2;

        this.initCarousel();
        this.bindCarouselEvents();
      }

      bindModalEvents() {
        this.galleryItems?.forEach((item) => {
          item.addEventListener("click", this.onGalleryItemClick);
          item.addEventListener("pointerenter", this.onGalleryItemWarm);
          item.addEventListener("touchstart", this.onGalleryItemWarm, { passive: true });
        });

        this.closeButton?.addEventListener("click", this.onCloseClick);
        this.modalPrevButton?.addEventListener("click", this.onModalPrevClick);
        this.modalNextButton?.addEventListener("click", this.onModalNextClick);
        this.modal?.addEventListener("click", this.onModalClick);
        this.modalShell?.addEventListener("touchstart", this.onModalTouchStart, { passive: true });
        this.modalShell?.addEventListener("touchend", this.onModalTouchEnd, { passive: true });
      }

      bindCarouselEvents() {
        this.prevButton?.addEventListener("click", this.onPrevClick);
        this.nextButton?.addEventListener("click", this.onNextClick);
        window.addEventListener("resize", this.onResize);
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.onResize);
        this.prevButton?.removeEventListener("click", this.onPrevClick);
        this.nextButton?.removeEventListener("click", this.onNextClick);
        this.galleryItems?.forEach((item) => {
          item.removeEventListener("click", this.onGalleryItemClick);
          item.removeEventListener("pointerenter", this.onGalleryItemWarm);
          item.removeEventListener("touchstart", this.onGalleryItemWarm);
        });
        this.closeButton?.removeEventListener("click", this.onCloseClick);
        this.modalPrevButton?.removeEventListener("click", this.onModalPrevClick);
        this.modalNextButton?.removeEventListener("click", this.onModalNextClick);
        this.modal?.removeEventListener("click", this.onModalClick);
        document.removeEventListener("keydown", this.onDocumentKeyDown);
        this.modalShell?.removeEventListener("touchstart", this.onModalTouchStart);
        this.modalShell?.removeEventListener("touchend", this.onModalTouchEnd);
        window.clearTimeout(this.modalOpenTimer);
        this.galleryObserver?.disconnect();
        this.flickity?.off("select", this.syncButtons);
        this.flickity?.off("settle", this.syncButtons);
        this.flickity?.off("select", this.onFlickitySelectPreload);
        this.flickity?.off("staticClick", this.onStaticClick);
        this.flickity?.destroy();
      }

      initCarousel() {
        if (this.mobileStaticGrid && this.isMobile()) {
          this.destroyCarousel();
          this.enableStaticGrid();
          return;
        }

        this.disableStaticGrid();

        if (this.flickity) {
          this.flickity.destroy();
        }

        this.updateColumnVariable();
        this.carousel.style.display = "block";

        const showPagination = this.shouldShowPagination();

        this.flickity = new Flickity(this.carousel, {
          cellAlign: "left",
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          pageDots: showPagination,
          wrapAround: false,
          cellSelector: ".ppc-gallery-cell",
          groupCells: this.getGroupCells(),
          accessibility: false,
          selectedAttraction: 0.025,
          friction: 0.28,
        });

        this.syncButtons = () => this.updateButtons();
        this.onStaticClick = (event, pointer, cellElement) => {
          if (!this.enableModal || !cellElement) {
            return;
          }

          const trigger = cellElement.querySelector("[data-gallery-full]");
          if (trigger) {
            this.openModalFromTrigger(trigger);
          }
        };

        this.flickity.on("select", this.syncButtons);
        this.flickity.on("settle", this.syncButtons);
        this.flickity.on("select", this.onFlickitySelectPreload);
        this.flickity.on("staticClick", this.onStaticClick);
        this.bindImageLoadResize();
        this.updateButtons();
        this.updateArrowVisibility();
        this.updatePaginationVisibility();
      }

      destroyCarousel() {
        if (!this.flickity) {
          return;
        }

        this.flickity.off("select", this.syncButtons);
        this.flickity.off("settle", this.syncButtons);
        this.flickity.off("select", this.onFlickitySelectPreload);
        this.flickity.off("staticClick", this.onStaticClick);
        this.flickity.destroy();
        this.flickity = null;
      }

      enableStaticGrid() {
        if (!this.carousel) {
          return;
        }

        this.style.setProperty(
          "--ppc-gallery-columns-current",
          this.columnsMobile || 6,
        );
        this.carousel.classList.add("ppc-gallery-carousel-static");
        this.classList.remove("ppc-gallery-has-arrows", "ppc-gallery-has-dots");
      }

      disableStaticGrid() {
        this.carousel?.classList.remove("ppc-gallery-carousel-static");
      }

      shouldShowPagination() {
        if (this.mobileStaticGrid && this.isMobile()) {
          return false;
        }

        const totalCells = this.carousel?.querySelectorAll(".ppc-gallery-cell").length || 0;
        return totalCells > this.getVisibleColumns();
      }

      updatePaginationVisibility() {
        this.classList.toggle("ppc-gallery-has-dots", this.shouldShowPagination());
      }

      bindImageLoadResize() {
        this.carousel?.querySelectorAll(".ppc-gallery-image img").forEach((image) => {
          if (image.complete) {
            return;
          }

          image.addEventListener(
            "load",
            () => {
              this.flickity?.resize();
            },
            { once: true },
          );
        });
      }

      onResize() {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          this.initCarousel();
        }, 150);
      }

      onPrevClick(event) {
        event.preventDefault();
        this.flickity?.previous();
      }

      onNextClick(event) {
        event.preventDefault();
        this.flickity?.next();
      }

      onGalleryItemClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.openModalFromTrigger(event.currentTarget);
      }

      onGalleryItemWarm(event) {
        const trigger = event.currentTarget;
        const index = this.galleryItems.indexOf(trigger);

        if (index === -1) {
          return;
        }

        this.preloadFullImage(index);
        this.preloadFullImage(index - 1);
        this.preloadFullImage(index + 1);
      }

      scheduleModalPreload() {
        if (!this.galleryItems.length) {
          return;
        }

        const preloadInitial = () => {
          if (this.isMobile()) {
            this.preloadAllImages();
            return;
          }

          const limit = Math.min(this.galleryItems.length, 8);

          for (let index = 0; index < limit; index += 1) {
            this.preloadFullImage(index);
          }
        };

        if (this.isMobile()) {
          window.setTimeout(preloadInitial, 120);
          return;
        }

        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(preloadInitial, { timeout: 2000 });
        } else {
          window.setTimeout(preloadInitial, 300);
        }
      }

      observeGalleryVisibility() {
        if (!this.enableModal || this.galleryObserver || typeof IntersectionObserver === "undefined") {
          return;
        }

        this.galleryObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              if (this.isMobile()) {
                this.preloadAllImages();
              } else {
                this.preloadFlickitySlide(this.flickity?.selectedIndex || 0);
              }
            });
          },
          { rootMargin: "240px 0px", threshold: 0.01 },
        );

        this.galleryObserver.observe(this);
      }

      onFlickitySelectPreload() {
        if (!this.isMobile() || !this.flickity) {
          return;
        }

        this.preloadFlickitySlide(this.flickity.selectedIndex);
      }

      preloadFlickitySlide(slideIndex = 0) {
        const columns = this.getVisibleColumns();
        const start = Math.max(0, slideIndex * columns);
        const end = Math.min(this.galleryItems.length, start + columns + 2);

        for (let index = start; index < end; index += 1) {
          this.preloadFullImage(index);
        }
      }

      preloadAllImages() {
        for (let index = 0; index < this.galleryItems.length; index += 1) {
          this.preloadFullImage(index);
        }
      }

      getFullSrc(trigger) {
        const desktopSrc = trigger?.getAttribute("data-gallery-full") || "";
        const mobileSrc = trigger?.getAttribute("data-gallery-full-mobile") || "";

        if (this.isMobile() && mobileSrc) {
          return mobileSrc;
        }

        return desktopSrc;
      }

      getPreviewSrc(trigger) {
        const thumb = trigger.querySelector("img");

        if (thumb?.complete && thumb.naturalWidth > 0) {
          return thumb.currentSrc || thumb.src;
        }

        return (
          trigger.getAttribute("data-gallery-preview") ||
          thumb?.currentSrc ||
          thumb?.src ||
          ""
        );
      }

      isSameImageUrl(left, right) {
        if (!left || !right) {
          return false;
        }

        try {
          return (
            new URL(left, window.location.href).href === new URL(right, window.location.href).href
          );
        } catch {
          return left === right;
        }
      }

      isImageCached(src) {
        const cached = this.preloadedImages.get(src);
        return Boolean(
          cached &&
            !(cached instanceof Promise) &&
            cached.complete &&
            cached.naturalWidth > 0,
        );
      }

      preloadFullImage(index) {
        if (index < 0 || index >= this.galleryItems.length) {
          return Promise.resolve("");
        }

        const trigger = this.galleryItems[index];
        const fullSrc = this.getFullSrc(trigger);

        if (!fullSrc) {
          return Promise.resolve("");
        }

        const cached = this.preloadedImages.get(fullSrc);
        if (cached instanceof Promise) {
          return cached;
        }

        if (this.isImageCached(fullSrc)) {
          return Promise.resolve(fullSrc);
        }

        const loader = new Promise((resolve) => {
          const image = new Image();
          image.decoding = "async";
          image.onload = () => {
            this.preloadedImages.set(fullSrc, image);
            resolve(fullSrc);
          };
          image.onerror = () => {
            this.preloadedImages.set(fullSrc, image);
            resolve(fullSrc);
          };
          image.src = fullSrc;
        });

        this.preloadedImages.set(fullSrc, loader);
        return loader;
      }

      setModalImageSrc(src) {
        if (!this.modalImage || !src) {
          return;
        }

        const current = this.modalImage.currentSrc || this.modalImage.src;

        if (this.isSameImageUrl(current, src)) {
          return;
        }

        this.modalImage.src = src;
      }

      loadModalImage(index) {
        const trigger = this.galleryItems[index];
        const fullSrc = this.getFullSrc(trigger);
        const previewSrc = this.getPreviewSrc(trigger);

        if (!fullSrc || !this.modalImage) {
          return;
        }

        if (previewSrc && !this.isSameImageUrl(previewSrc, fullSrc)) {
          this.setModalImageSrc(previewSrc);
        }

        if (!previewSrc || this.isSameImageUrl(previewSrc, fullSrc) || this.isImageCached(fullSrc)) {
          this.setModalImageSrc(fullSrc);
          this.modalImage.classList.remove("is-loading-full");
          return;
        }

        this.modalImage.classList.add("is-loading-full");

        this.preloadFullImage(index).then(() => {
          if (this.currentModalIndex !== index || !this.modal?.classList.contains("is-open")) {
            return;
          }

          this.setModalImageSrc(fullSrc);
          this.modalImage.classList.remove("is-loading-full");
        });
      }

      onModalClick(event) {
        if (!this.modal?.classList.contains("is-open") || this.modalIgnoreClick) {
          return;
        }

        if (
          event.target.closest(".js-modal-prev") ||
          event.target.closest(".js-modal-next")
        ) {
          return;
        }

        this.hideModal();
      }

      onModalPrevClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.showPreviousModalImage();
      }

      onModalNextClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.showNextModalImage();
      }

      onDocumentKeyDown(event) {
        if (!this.modal?.classList.contains("is-open")) {
          return;
        }

        const code = event.code?.toUpperCase();

        if (code === "ESCAPE") {
          event.preventDefault();
          this.hideModal();
          return;
        }

        if (code === "ARROWLEFT") {
          event.preventDefault();
          this.showPreviousModalImage();
          return;
        }

        if (code === "ARROWRIGHT") {
          event.preventDefault();
          this.showNextModalImage();
        }
      }

      onModalTouchStart(event) {
        if (!this.modal?.classList.contains("is-open") || event.touches.length !== 1) {
          return;
        }

        this.modalTouchStartX = event.touches[0].clientX;
        this.modalTouchStartY = event.touches[0].clientY;
      }

      onModalTouchEnd(event) {
        if (!this.modal?.classList.contains("is-open") || !this.isMobile()) {
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.modalTouchStartX;
        const deltaY = touch.clientY - this.modalTouchStartY;

        if (Math.abs(deltaX) < 28 || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }

        this.modalIgnoreClick = true;
        window.setTimeout(() => {
          this.modalIgnoreClick = false;
        }, 400);

        if (deltaX > 0) {
          this.showPreviousModalImage();
        } else {
          this.showNextModalImage();
        }
      }

      showPreviousModalImage() {
        if (this.currentModalIndex <= 0) {
          return;
        }

        this.openModalAtIndex(this.currentModalIndex - 1);
      }

      showNextModalImage() {
        if (this.currentModalIndex >= this.galleryItems.length - 1) {
          return;
        }

        this.openModalAtIndex(this.currentModalIndex + 1);
      }

      openModalFromTrigger(trigger) {
        const index = this.galleryItems.indexOf(trigger);

        if (index === -1) {
          return;
        }

        this.openModalAtIndex(index, trigger);
      }

      openModalAtIndex(index, opener = this.galleryItems[index]) {
        const trigger = this.galleryItems[index];
        const fullSrc = this.getFullSrc(trigger);
        const label = trigger?.getAttribute("data-gallery-label") || "";

        if (!fullSrc || !this.modal || !this.modalImage) {
          return;
        }

        this.currentModalIndex = index;
        this.modalImage.alt = label;
        this.loadModalImage(index);
        this.updateModalNav();
        this.preloadFullImage(index - 1);
        this.preloadFullImage(index + 1);

        if (this.modal.classList.contains("is-open")) {
          return;
        }

        this.showModal(opener);
      }

      updateModalNav() {
        const total = this.galleryItems.length;
        const hasMultiple = total > 1;
        const mobile = this.isMobile();

        this.modal?.classList.toggle("ppc-gallery-modal-has-nav", hasMultiple && !mobile);
        this.modal?.classList.toggle("ppc-gallery-modal-swipe", hasMultiple && mobile);

        if (!this.modalPrevButton || !this.modalNextButton) {
          return;
        }

        if (mobile || !hasMultiple) {
          this.modalPrevButton.hidden = true;
          this.modalNextButton.hidden = true;
          return;
        }

        this.modalPrevButton.hidden = false;
        this.modalNextButton.hidden = false;
        this.setModalNavButtonState(this.modalPrevButton, this.currentModalIndex <= 0);
        this.setModalNavButtonState(
          this.modalNextButton,
          this.currentModalIndex >= total - 1,
        );
      }

      setModalNavButtonState(button, isDisabled) {
        button.setAttribute("aria-disabled", isDisabled ? "true" : "false");
        button.classList.toggle("pill-nav-item-disabled", isDisabled);
      }

      showModal(opener) {
        if (!this.modal) {
          return;
        }

        this.openedBy = opener;
        this.modalIgnoreClick = true;
        window.clearTimeout(this.modalOpenTimer);
        this.modalOpenTimer = window.setTimeout(() => {
          this.modalIgnoreClick = false;
        }, 400);

        document.body.classList.add("overflow-hidden");
        this.modal.classList.add("is-open");
        this.modal.setAttribute("aria-hidden", "false");
        document.addEventListener("keydown", this.onDocumentKeyDown);

        if (typeof trapFocus === "function") {
          trapFocus(this.modal, this.modalShell || this.closeButton);
        } else {
          this.closeButton?.focus();
        }
      }

      hideModal() {
        if (!this.modal) {
          return;
        }

        window.clearTimeout(this.modalOpenTimer);
        this.modalIgnoreClick = false;

        document.body.classList.remove("overflow-hidden");
        this.modal.classList.remove("is-open");
        this.modal.setAttribute("aria-hidden", "true");
        document.removeEventListener("keydown", this.onDocumentKeyDown);

        if (typeof removeTrapFocus === "function" && this.openedBy) {
          removeTrapFocus(this.openedBy);
        }

        this.openedBy = null;
      }

      getGroupCells() {
        return this.getVisibleColumns();
      }

      getVisibleColumns() {
        const width = window.innerWidth;

        if (width >= 990) {
          return this.columnsDesktop;
        }

        if (width >= 750) {
          return this.columnsTablet;
        }

        return this.columnsMobile;
      }

      updateColumnVariable() {
        const columns = this.getVisibleColumns();
        this.style.setProperty("--ppc-gallery-columns-current", columns);
      }

      updateArrowVisibility() {
        if (this.mobileStaticGrid && this.isMobile()) {
          this.classList.remove("ppc-gallery-has-arrows", "ppc-gallery-has-dots");
          return;
        }

        const totalCells = this.carousel?.querySelectorAll(".ppc-gallery-cell").length || 0;
        const needsArrows = totalCells > this.getVisibleColumns() && !this.isMobile();
        this.classList.toggle("ppc-gallery-has-arrows", needsArrows);
        this.updatePaginationVisibility();
      }

      isMobile() {
        return window.innerWidth < 750;
      }

      updateButtons() {
        if (!this.flickity || !this.prevButton || !this.nextButton) {
          return;
        }

        const totalCells = this.carousel?.querySelectorAll(".ppc-gallery-cell").length || 0;
        const needsArrows = totalCells > this.getVisibleColumns() && !this.isMobile();

        if (!needsArrows) {
          this.prevButton.setAttribute("aria-hidden", "true");
          this.nextButton.setAttribute("aria-hidden", "true");
          return;
        }

        this.prevButton.setAttribute("aria-hidden", "false");
        this.nextButton.setAttribute("aria-hidden", "false");
        this.enableButton(this.prevButton);
        this.enableButton(this.nextButton);

        const slides = this.flickity.slides;
        if (!slides || slides.length === 0) {
          return;
        }

        const current = slides[this.flickity.selectedIndex];
        const first = slides[0];
        const last = slides[slides.length - 1];

        if (current.target === first.target) {
          this.disableButton(this.prevButton);
        }

        if (current.target === last.target) {
          this.disableButton(this.nextButton);
        }
      }

      enableButton(button) {
        button.classList.remove("pill-nav-item-disabled", "focus-none");
        button.setAttribute("aria-disabled", "false");
      }

      disableButton(button) {
        button.classList.add("pill-nav-item-disabled", "focus-none");
        button.setAttribute("aria-disabled", "true");
      }
    },
  );
}
